import { create } from 'zustand'
import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import CryptoJS from 'crypto-js'

// Note: Do NOT add 'devTools' after 'zustand/middleware' or remove 'immer' from 'zustand/middleware/immer'
// Refs: https://github.com/pmndrs/zustand/pull/915, https://github.com/pmndrs/zustand/discussions/2797
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { uploadApi } from './api'
import { createEventEmitter } from './eventEmitter'
import { PASSWORD_STATUSES, POLLING_CONFIG, UPLOAD_STAGES } from 'components/utility/sandbox_upload/constants'
import { isEmpty } from 'lodash-es'
import * as pollingUtils from './pollingUtils'
import { activePollingRefs, clearPollingTimeout, pollingStartTimeRefs, pollingTimeoutRefs } from './pollingUtils'
import { parseBackendError } from 'components/utility/sandbox_upload/errorUtils'
import { shallow } from 'zustand/vanilla/shallow'

const TIMED_OUT_ERROR_MESSAGE = 'failed to process'

const INITIAL_FILE_STATE = {
  progress: 0,
  metadata: {},
  encryption: {
    isEncrypted: false,
    passwordStatus: null, // 'required', 'verifying', 'incorrect', 'verified', null
  },
}

// Create an event emitter for our store actions
const storeEvents = createEventEmitter()

async function calculateMD5(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const buffer = e.target.result
      const wordArray = CryptoJS.lib.WordArray.create(buffer)
      const md5 = CryptoJS.MD5(wordArray)
      resolve(CryptoJS.enc.Base64.stringify(md5))
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

function validateFile(file, { accepts, fileSizes }) {
  let [valid, errors] = [true, []]
  const fileName = file.name
  const fileExt = fileName.substring(fileName.lastIndexOf('.')).toLowerCase()

  // Check file extension
  if (accepts) {
    if (!accepts.includes(fileExt)) {
      valid = false
      errors.push({ message: `File type ${fileExt} is not supported. Accepted types: ${accepts.join(', ')}` })
    }
  }

  // Check file size
  if (fileSizes) {
    // Get the size limit - use specific extension limit or default to '*'
    const sizeLimit = fileSizes[fileExt] || fileSizes['*']

    if (sizeLimit && file.size > sizeLimit) {
      valid = false
      const sizeMB = sizeLimit / (1024 * 1024)
      errors.push({ message: `File size exceeds the limit of ${sizeMB.toFixed(2)} MB` })
    }
  }

  return [valid, errors.length > 0 ? errors : null]
}

const useUploadersStore = create(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        uploaders: {},

        initUploader: (uploaderUUID, { accepts, fileSizes, multiple, existingFiles, conversionFlags }) => {
          if (get().uploaders[uploaderUUID]) {
            console.log(`uploader uuid ${uploaderUUID} was already initialized!`)
            return
          }

          storeEvents.emit('action', {
            type: 'INIT_UPLOADER',
            payload: {
              uploaderUUID,
              accepts,
            },
          })

          set((state) => {
            state.uploaders[uploaderUUID] = {
              files: {},
              errors: {},
              queue: [],
              activeUploads: [],
              failedItems: [],
              reProcessItems: [],
              component: { accepts, fileSizes, multiple },
              conversionFlags: conversionFlags || {},
            }

            if (!isEmpty(existingFiles)) {
              Object.entries(existingFiles).forEach(([key, value]) => {
                state.uploaders[uploaderUUID].files[key] = {
                  ...value,
                  stage: value.stage || 'completed',
                }
              })
            }
          })
        },

        addFiles: (uploaderUUID, files) => {
          set((state) => {
            const uploader = state.uploaders[uploaderUUID]
            if (!uploader) return

            files.forEach((file) => {
              const [valid, errors] = validateFile(file, uploader.component)
              const fileUUID = uuidv4()
              if (valid) {
                uploader.files[fileUUID] = {
                  ...INITIAL_FILE_STATE,
                  file,
                  stage: 'uploading',
                  timestamp: new Date().toISOString(),
                }
                uploader.queue.push(fileUUID)
                storeEvents.emit('action', { type: 'ADD_FILE', payload: { uploaderUUID, fileUUID, file } })
              } else {
                uploader.files[fileUUID] = {
                  ...INITIAL_FILE_STATE,
                  file,
                  stage: 'failed',
                  timestamp: new Date().toISOString(),
                }
                storeEvents.emit('action', { type: 'ADD_FILE', payload: { uploaderUUID, fileUUID, file } })

                const error = errors[0]
                state.uploaders[uploaderUUID].errors[fileUUID] = error
                storeEvents.emit('action', { type: 'SET_ERROR', payload: { uploaderUUID, fileUUID, error } })
              }
            })
          })
        },

        removeFile: (uploaderUUID, fileUUID) => {
          set((state) => {
            if (state.uploaders[uploaderUUID]) {
              delete state.uploaders[uploaderUUID].files[fileUUID]
              delete state.uploaders[uploaderUUID].errors[fileUUID]
              state.uploaders[uploaderUUID].queue = state.uploaders[uploaderUUID].queue.filter((id) => id !== fileUUID)
              state.uploaders[uploaderUUID].activeUploads = state.uploaders[uploaderUUID].activeUploads.filter(
                (id) => id !== fileUUID
              )
              state.uploaders[uploaderUUID].failedItems = state.uploaders[uploaderUUID].failedItems.filter(
                (id) => id !== fileUUID
              )
            }
          })
          storeEvents.emit('action', {
            type: 'REMOVE_FILE',
            payload: {
              uploaderUUID,
              fileUUID,
            },
          })
        },

        addUploadedFiles: (uploaderUUID, uploadedFiles) => {
          set((state) => {
            const uploader = state.uploaders[uploaderUUID]
            if (!uploader) return

            Object.entries(uploadedFiles).forEach(([fileUUID, fileData]) => {
              uploader.files[fileUUID] = {
                ...INITIAL_FILE_STATE,
                ...fileData,
                stage: fileData.stage || UPLOAD_STAGES.COMPLETED,
                timestamp: fileData.timestamp || new Date().toISOString(),
              }
            })
          })
        },

        removeAllFiles: (uploaderUUID, emit = true) => {
          set((state) => {
            if (state.uploaders[uploaderUUID]) {
              const fileUUIDs = Object.keys(state.uploaders[uploaderUUID].files)

              // Clear all files and related data
              state.uploaders[uploaderUUID].files = {}
              state.uploaders[uploaderUUID].errors = {}
              state.uploaders[uploaderUUID].queue = []
              state.uploaders[uploaderUUID].activeUploads = []
              state.uploaders[uploaderUUID].failedItems = []
              state.uploaders[uploaderUUID].reProcessItems = []

              if (emit) {
                // Emit remove events for each file
                fileUUIDs.forEach((fileUUID) => {
                  storeEvents.emit('action', {
                    type: 'REMOVE_FILE',
                    payload: {
                      uploaderUUID,
                      fileUUID,
                    },
                  })
                })
              }
            }
          })
        },

        updateProgress: (uploaderUUID, fileUUID, progress) => {
          set((state) => {
            if (state.uploaders[uploaderUUID] && state.uploaders[uploaderUUID].files[fileUUID]) {
              state.uploaders[uploaderUUID].files[fileUUID].progress = progress
            }
          })
          storeEvents.emit('action', {
            type: 'UPDATE_PROGRESS',
            payload: {
              uploaderUUID,
              fileUUID,
              progress,
            },
          })
        },

        updateStatus: (uploaderUUID, fileUUID, stage, metadata = null) => {
          set((state) => {
            const uploader = state.uploaders[uploaderUUID]
            if (uploader.files[fileUUID]) {
              const fileState = uploader.files[fileUUID]

              fileState.stage = stage
              if (metadata) fileState.metadata = metadata

              if (
                stage === UPLOAD_STAGES.COMPLETED &&
                fileState.encryption.isEncrypted === true &&
                fileState.encryption.passwordStatus === PASSWORD_STATUSES.VERIFYING
              ) {
                fileState.encryption.passwordStatus = PASSWORD_STATUSES.VERIFIED
              }
            }
          })
          storeEvents.emit('action', {
            type: 'UPDATE_STATUS',
            payload: {
              uploaderUUID,
              fileUUID,
              stage,
              metadata,
            },
          })
        },

        updatePasswordStatus: (uploaderUUID, fileUUID, passwordStatus, isEncrypted) => {
          set((state) => {
            if (state.uploaders[uploaderUUID] && state.uploaders[uploaderUUID].files[fileUUID]) {
              state.uploaders[uploaderUUID].files[fileUUID].encryption.passwordStatus = passwordStatus
              state.uploaders[uploaderUUID].files[fileUUID].encryption.isEncrypted = isEncrypted
            }
          })
        },

        setError: (uploaderUUID, fileUUID, error) => {
          set((state) => {
            if (state.uploaders[uploaderUUID]) {
              state.uploaders[uploaderUUID].errors[fileUUID] = error
            }
          })
          storeEvents.emit('action', {
            type: 'SET_ERROR',
            payload: {
              uploaderUUID,
              fileUUID,
              error,
            },
          })
        },

        upload: async (uploaderUUID, fileUUID) => {
          const file = get().uploaders[uploaderUUID]?.files?.[fileUUID]?.file
          const conversionFlags = get().uploaders[uploaderUUID].conversionFlags
          if (!file) return

          const md5 = await calculateMD5(file)
          const fileData = {
            uuid: fileUUID,
            name: file.name,
            type: file.type,
            md5: md5,
            conversionFlags: conversionFlags,
          }

          try {
            const { url, fields, metadata_path } = await uploadApi.generatePolicy(fileData)
            await uploadApi.uploadFile(url, fields, file, md5, (progress) => {
              get().updateProgress(uploaderUUID, fileUUID, progress)
            })
            return { success: true }
          } catch (error) {
            get().setError(uploaderUUID, fileUUID, error)
            get().updateStatus(uploaderUUID, fileUUID, 'failed')
            return { success: false, error }
          }
        },

        submitPassword: async (uploaderUUID, fileUUID, password) => {
          if (!get().uploaders[uploaderUUID].files[fileUUID]) return

          try {
            await uploadApi.submitPassword(fileUUID, password)
            set((state) => {
              state.uploaders[uploaderUUID].files[fileUUID].encryption.passwordStatus = PASSWORD_STATUSES.VERIFYING
            })
            get().reProcess(uploaderUUID, fileUUID)
            return { success: true }
          } catch (error) {
            return { success: false, error }
          }
        },

        moveQueueToActive: (uploaderUUID, count = 1) => {
          set((state) => {
            if (!state.uploaders[uploaderUUID]) return
            const queue = state.uploaders[uploaderUUID].queue
            const activeUploads = state.uploaders[uploaderUUID].activeUploads
            const toMove = queue.splice(0, Math.min(count, queue.length))
            activeUploads.push(...toMove)
          })
        },

        removeFromActiveUploads: (uploaderUUID, fileUUID) => {
          set((state) => {
            if (!state.uploaders[uploaderUUID]) return
            state.uploaders[uploaderUUID].activeUploads = state.uploaders[uploaderUUID].activeUploads.filter(
              (id) => id !== fileUUID
            )
          })
        },

        addToFailedItems: (uploaderUUID, fileUUID) => {
          set((state) => {
            if (!state.uploaders[uploaderUUID]) return
            state.uploaders[uploaderUUID].failedItems.push(fileUUID)
          })
        },

        removeFromFailedItems: (uploaderUUID, fileUUID) => {
          set((state) => {
            if (!state.uploaders[uploaderUUID]) return
            state.uploaders[uploaderUUID].failedItems = state.uploaders[uploaderUUID].failedItems.filter(
              (id) => id !== fileUUID
            )
          })
        },

        addToReprocessItems: (uploaderUUID, fileUUID) => {
          set((state) => {
            if (!state.uploaders[uploaderUUID]) return
            state.uploaders[uploaderUUID].reProcessItems.push(fileUUID)
          })
        },

        removeFromReProcessItems: (uploaderUUID, fileUUID) => {
          set((state) => {
            if (!state.uploaders[uploaderUUID]) return
            state.uploaders[uploaderUUID].reProcessItems = state.uploaders[uploaderUUID].reProcessItems.filter(
              (id) => id !== fileUUID
            )
          })
        },

        reProcess: (uploaderUUID, fileUUID) => {
          get().removeFromFailedItems(uploaderUUID, fileUUID)
          get().addToReprocessItems(uploaderUUID, fileUUID)
        },

        processQueue: async (uploaderUUID, batchSize = 3) => {
          if (!get().uploaders[uploaderUUID]) return

          let queueLength = get().uploaders[uploaderUUID].queue.length

          while (queueLength > 0) {
            get().moveQueueToActive(uploaderUUID, batchSize)
            const activeUploads = [...get().uploaders[uploaderUUID].activeUploads]
            await Promise.all(
              activeUploads.map(async (fileUUID) => {
                const result = await get().upload(uploaderUUID, fileUUID)
                if (result.success) {
                  get().removeFromActiveUploads(uploaderUUID, fileUUID)
                } else {
                  get().removeFromActiveUploads(uploaderUUID, fileUUID)
                  get().addToFailedItems(uploaderUUID, fileUUID)
                }
              })
            )

            queueLength = get().uploaders[uploaderUUID].queue.length
          }
        },

        startPolling: (uploaderUUID, uuid) => {
          if (activePollingRefs.has(uuid)) return

          activePollingRefs.add(uuid)
          clearPollingTimeout(uuid)
          pollingStartTimeRefs[uuid] = Date.now()

          const pollStatus = async () => {
            try {
              let status
              try {
                status = await uploadApi.checkStatus(uuid)
              } catch (error) {
                // A failed or timed-out status request is retried within the round's time limit
                if (Date.now() - pollingStartTimeRefs[uuid] < POLLING_CONFIG.MAX_DURATION.PROCESSING) {
                  pollingTimeoutRefs[uuid] = setTimeout(pollStatus, POLLING_CONFIG.INTERVAL)
                  return
                }
                throw error
              }

              // Only update status if we have real metadata
              if (status.processing_status !== 'pending') {
                const stage = Object.values(UPLOAD_STAGES).includes(status.processing_status)
                  ? status.processing_status
                  : 'unknown'
                get().updateStatus(uploaderUUID, uuid, stage, status)
              }

              // Check if we should continue polling
              const elapsedTime = Date.now() - pollingStartTimeRefs[uuid]
              const maxDuration =
                status.processing_status === 'processing'
                  ? POLLING_CONFIG.MAX_DURATION.PROCESSING
                  : POLLING_CONFIG.MAX_DURATION.METADATA

              if (['pending', 'processing'].includes(status.processing_status)) {
                if (elapsedTime < maxDuration) {
                  pollingTimeoutRefs[uuid] = setTimeout(pollStatus, POLLING_CONFIG.INTERVAL)
                } else {
                  activePollingRefs.delete(uuid)
                  get().setError(uploaderUUID, uuid, {
                    code: 'TIMED_OUT',
                    message: TIMED_OUT_ERROR_MESSAGE,
                    details: null,
                  })
                  get().updateStatus(uploaderUUID, uuid, UPLOAD_STAGES.FAILED, status)
                }
              } else {
                activePollingRefs.delete(uuid)
              }

              if (status.processing_status === 'failed') {
                const errorCode = status.error_message?.error_details?.code
                const error = parseBackendError(errorCode)

                if (error.code === 'PASSWORD_REQUIRED') {
                  get().updateStatus(uploaderUUID, uuid, UPLOAD_STAGES.AWAITING_PASSWORD, status)
                  get().updatePasswordStatus(uploaderUUID, uuid, PASSWORD_STATUSES.REQUIRED)
                } else if (error.code === 'PASSWORD_MISMATCHED') {
                  get().updateStatus(uploaderUUID, uuid, UPLOAD_STAGES.AWAITING_PASSWORD, status)
                  get().updatePasswordStatus(uploaderUUID, uuid, PASSWORD_STATUSES.INCORRECT)
                }

                get().setError(uploaderUUID, uuid, error)
              }
            } catch (error) {
              activePollingRefs.delete(uuid)
              get().setError(uploaderUUID, uuid, error)
            }
          }

          pollStatus()
        },

        startPollingIfNeeded: async (uploaderUUID) => {
          get().uploaders[uploaderUUID]?.activeUploads?.forEach((uuid) => {
            const fileInfo = get().uploaders[uploaderUUID].files[uuid]
            if (fileInfo.stage === 'processing' || (fileInfo.stage === 'uploading' && fileInfo.progress === 100)) {
              get().startPolling(uploaderUUID, uuid)
            }
          })
        },

        initSubscriptions: (uploaderUUID, { queueProcessingBatchSize }) => {
          // Set up queue processing subscription
          const queueUnsubscribe = useUploadersStore.subscribe(
            (state) => state.uploaders[uploaderUUID]?.queue?.length,
            (queueLength) => {
              if (queueLength > 0) {
                get().processQueue(uploaderUUID, queueProcessingBatchSize)
              }
            }
          )

          // Set up active uploads subscription
          const activeUploadsUnsubscribe = useUploadersStore.subscribe(
            (state) => [state.uploaders[uploaderUUID]?.activeUploads, state.uploaders[uploaderUUID]?.files],
            () => {
              get().startPollingIfNeeded(uploaderUUID)
            },
            shallow
          )

          // Set up reprocess items subscription
          const reprocessUnsubscribe = useUploadersStore.subscribe(
            (state) => state.uploaders[uploaderUUID]?.reProcessItems,
            (reprocessItems) => {
              reprocessItems.forEach((fileUUID) => {
                get().startPolling(uploaderUUID, fileUUID)
              })
            }
          )

          // Store unsubscribe functions (optional - for cleanup)
          set((state) => {
            state.uploaders[uploaderUUID]._subscriptions = {
              queue: queueUnsubscribe,
              activeUploads: activeUploadsUnsubscribe,
              reprocess: reprocessUnsubscribe,
            }
          })
        },
      }))
    ),
    {
      name: 'uploaders-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
)

export function useStoreActions(callback) {
  useEffect(() => {
    // Subscribe to action events
    return storeEvents.on('action', callback)
  }, [callback])
}

export default useUploadersStore
